import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import history from '~/browserHistory';
import { useUserSelector } from '~/hooks/useUserSelector';
import { useTrackEvent } from '~/hooks/useTrackEvent';
import { POSTHOG_EVENTS } from '~/services/posthog';
import { fireV0RequestSubmitted } from '~/posthogs/events';
import { routes } from '~/routes';
import {
  closeOverlappingModal,
  editJobInfoMdb,
  hideJobCreatePaymentError,
  postInviteToMultiDayJob,
} from '../../../../actions';
import { isDailyOvertime } from '../utils';

export const useConfirmStep = () => {
  const dispatch = useDispatch();
  const trackPosthogEvent = useTrackEvent();
  const [showLeavingPopup, setShowLeavingPopup] = useState(false);
  const navigate = useNavigate();
  const jobInfo = useSelector((state) => state.multiday.jobInfo);
  const job = useSelector((state) => state.job);
  const showJobCreatePaymentError = useSelector((state) => state.job.showJobCreatePaymentError);
  const user = useUserSelector();
  const workerClassificationType = user?.workerClassification?.classificationType;
  const overtimeLimits = user?.overtimeLimits?.[workerClassificationType];
  const hasOvertimeDay = jobInfo.some((job) => isDailyOvertime(job, overtimeLimits?.daily));
  const [showOvertimeLimitPopup, setShowOvertimeLimitPopup] = useState(false);
  const officeId = jobInfo?.[0]?.officeId || user?.id;

  const handleConfirmButton = ({ alertOvertime = true }) => {
    if (alertOvertime && overtimeLimits && hasOvertimeDay) {
      setShowOvertimeLimitPopup(true);
      return;
    }

    // Track PostHog events
    trackPosthogEvent(POSTHOG_EVENTS.OFFICE_SHIFT_REQUEST_SUBMITTED);
    fireV0RequestSubmitted({ shiftsPosted: jobInfo.length });

    // Track invite event if professionals are being invited
    if (jobInfo[0].professional?.id) {
      trackPosthogEvent(POSTHOG_EVENTS.OFFICE_INVITE_SENT, {
        source: 'request_shift',
        invite_type: 're_book',
        location_category: 'request_form',
        professional_id: jobInfo[0].professional.id,
        professional_name: jobInfo[0].professional.name,
        office_id: officeId,
        shift_count: jobInfo.length,
      });
    }

    dispatch(postInviteToMultiDayJob(officeId));
  };

  const confirmOverlapping = () => {
    for (let i = 0; i < jobInfo.length; i += 1) {
      const jobData = jobInfo[i];
      dispatch(editJobInfoMdb(jobData.localDate, { ...jobData, duplicate: true }));
    }
    dispatch(closeOverlappingModal());
    handleConfirmButton({ alertOvertime: false });
  };

  const cancelRequest = () => {
    history.push('/');
  };

  const hidePaymentError = () => {
    navigate(-1);
    dispatch(hideJobCreatePaymentError());
  };

  const updatePayment = () => {
    history.push(routes.paymentInformation);
    dispatch(hideJobCreatePaymentError());
  };

  return {
    jobInfo,
    job,
    showLeavingPopup,
    showJobCreatePaymentError,
    setShowLeavingPopup,
    handleConfirmButton,
    confirmOverlapping,
    cancelRequest,
    hidePaymentError,
    updatePayment,
    showOvertimeLimitPopup,
    setShowOvertimeLimitPopup,
  };
};
